import {
  subHours,
  subWeeks,
  subMonths,
  subYears,
  format,
  formatDistanceToNowStrict,
  endOfDay,
  addDays
} from "date-fns";
import isWithinRange from "date-fns/isWithinInterval";

import {
  LookBackDate,
  LookBackOptions,
  UserProfileData,
  CaseDateFormat,
  PageLoadError,
  RiskPatternItem,
  SelectOption
} from "@/rca-types";
import {
  ICaseCountry,
  ICaseInfo,
  ICaseReasonCode,
  ICaseStore,
  IDataAPICases,
  IGroupedCaseReasonCode
} from "@/types/casesFrontendTypes";
import { AppUser } from "@/types/users";
import { User } from "@microsoft/microsoft-graph-types-beta";
import { INotification } from "@/types/notifications";
import CurrencyLocaleMapping, { Country } from "./data/currency-locale";
import { AxiosError } from "axios";
import moment from "moment";
import {
  AMOUNT_RANGE_OPTIONS,
  AMOUNT_REFERENCE_LIST,
  CONTROLS_SORT_BY,
  DATE_RANGE_OPTIONS,
  RC,
  RiskControlsCompensationTypes,
  RiskControlsRouteParamMap
} from "./constants";
import { RiskControlsMap } from "./constants";

export function computedClass(stacked: boolean): string {
  if (stacked) {
    return "d-flex flex-wrap flex-column align-content-stretch";
  } else {
    return "";
  }
}

/**
 * Type-predicate function which returns the verdict on
 * whether the input data is not of a 'nullish' type
 * @param input data of 'unknown' type
 */
export function isValidData<T extends unknown>(
  input: T
): input is T extends null | undefined ? never : T {
  return input != null;
}

export function isValidString(str: unknown): str is string {
  return typeof str === "string" && str.length > 0;
}

export function convertRatio2Percentage(ratio: unknown): number {
  if (typeof ratio === "number") {
    return ratio * 100;
  } else {
    return 0;
  }
}

export function decorateWithPercentage(value: number, locale: string): string {
  return formatDecimalPlaces(value, locale) + "%";
}

/**
 * This function returns a boolean indicating
 * whether or not the input-value can be successfully
 * 'cast to a number'
 *
 *  // let foo: any;
    // foo = null;
    // console.log("isValidNumber (null):", isValidNumber(foo) ? +foo : -1);
    // foo = undefined;
    // console.log("isValidNumber (undefined):", isValidNumber(foo) ? +foo : -1);
    // foo = NaN;
    // console.log("isValidNumber (NaN):", isValidNumber(foo) ? +foo : -1);
    // foo = ["foo", 1, null];
    // console.log("isValidNumber ([]):", isValidNumber(foo) ? +foo : -1);
    // foo = { foo: "bar" };
    // console.log("isValidNumber ({}):", isValidNumber(foo) ? +foo : -1);
    // foo = -2;
    // console.log("isValidNumber (number):", isValidNumber(foo) ? +foo : -1);
    // foo = "-2";
    // console.log("isValidNumber (string):", isValidNumber(foo) ? +foo : -1);
 * @param value Unknown input-value
 */
export function isValidNumber(value: unknown): value is number {
  return (
    (typeof value === "string" || typeof value === "number") && !isNaN(+value)
  );
}

// https://stackoverflow.com/questions/63541843
export function truthyFilter<T>(x: T | false | undefined | "" | 0): x is T {
  return !!x;
}

// Given an object, this confirm whether the key is a valid prop index of the object
export function hasKey<T extends Record<string, unknown>>(
  obj: T,
  key: string | number | symbol
): key is keyof T {
  return key in obj;
}

// For use as keys in grouped-notifications; to enable sorting
export function getEndOfDayValue(timestamp: Date): number {
  return endOfDay(timestamp).getTime();
}

export function getFormattedDate(
  timestamp: Date,
  desiredFormat?: CaseDateFormat
): string {
  switch (desiredFormat) {
    case "days-ago":
      /**
       * Used in the Notifications module
       * (See Figma design for sample)
       * Returns date in the format: '15m ago' | '1d ago'
       * @param timestamp Date; which gets formatted
       */
      return formatDistanceToNowStrict(timestamp, {
        addSuffix: true,
        unit: getTimeUnit(timestamp),
        roundingMethod: "ceil"
      });
    case "date-slash-time":
      /**
       * Used in the Timeline & Comments module
       * (See Figma design for sample)
       * Returns date in the format: 'Sep 6 / 22:15'
       * @param timestamp Date; which gets formatted
       */
      return `${format(timestamp, "LLL d")} / ${format(timestamp, "kk:mm")}`;
    case "date-slash-year":
      /**
       * Used in the Notifications module
       * (See Figma design for sample)
       * Returns date in the format: 'Sep 6 / 1997'
       * @param timestamp Date; which gets formatted
       */
      return `${format(timestamp, "LLL d")} / ${format(timestamp, "yyyy")}`;
    case "month-year":
      /**
       * Used in the CasePreview module: 'Case period'
       * (See Figma design for sample)
       * Returns date in the format: 'September 1997'
       * @param timestamp Date; which gets formatted
       */
      return format(timestamp, "LLLL yyyy");
    case "previous-month-range":
      /**
       * Used in the LookBackGraph's x-aixs tick labels
       * (See Figma design for sample)
       * Returns date in the format: '06.02 - 06.03'
       * to represent "between February 6th and March 6th"
       * @param timestamp Date; which gets formatted
       */
      return `${format(
        addDays(subMonths(timestamp, 1), 1),
        "dd.MM"
      )} - ${format(timestamp, "dd.MM")}`;
    default:
      /**
       * Used in the CasePreview module: 'Case created date'
       * (See Figma design for sample)
       *
       * Returns date in the format: '1997-03-06'
       * @param timestamp Date; which gets formatted
       *
       */
      return format(timestamp, "yyyy-MM-dd");
  }
}

/**
 * Get the last date before the month-range
 * of the passed-in date begins
 *
 * e.g. if 1997-06-03 is the passed-in date, its
 * month-range will be "1997-05-03 to 1997-06-03"; and
 * so the previous-month-date returned will be "1997-05-02"
 * @param input Date against which to calculate
 */
export function getPreviousMonthDate(input: Date): Date {
  return subMonths(input, 1);
}

/**
 * Sorter of array of Notification items.
 * Result is sorted in DESCENDING ORDER;
 * i.e. from most recent date to the earliest
 * @param current
 * @param next
 */
export function sortCaseNotifications(
  current: INotification,
  next: INotification
): number {
  // Create Date objects
  const currentDate = new Date(current.created);
  const nextDate = new Date(next.created);

  // Sanitizer / Error-handler
  const currentVal = currentDate != null ? currentDate.getTime() : 0;
  const nextVal = nextDate != null ? nextDate.getTime() : 0;

  // Ascending-order sort: currentval - nextVal
  // Descending-order sort = nextVal - currentVal

  return nextVal - currentVal;
}

export function getUserDetails(
  item: User,
  currentUser: UserProfileData
): AppUser {
  const { givenName, surname } = item;
  const userFullname = `${givenName} ${surname}`;

  const { firstname, lastname } = currentUser;
  const myFullname = `${firstname} ${lastname}`;

  return {
    fullname: userFullname,
    isCurrentUser: myFullname === userFullname
  };
}

/**
 * Returns a 2-decimal-place formatted
 * number-string of the passed-in value
 *
 * If the value is deemed to be invalid,
 * the fallback is '0'
 * @param value Input of 'unknown' type
 */
export function formatDecimalPlaces(value: unknown, locale: string): string {
  const sanitised = isValidNumber(value) ? +value : 0;

  return getLocaleCurrency(sanitised, locale, false);
}

/**
 * Given a value (of type LookBackOptions), return
 * the result of translating such value into a real 'Date' instance
 * @param lookBackString The custom-made string values used for the LookBack dropdown component
 * @param present The latest current date (i.e. updated each time at the point the Vuex Action is triggered)
 */
export function getRelativeDate(
  lookBackString: LookBackOptions,
  present: Date
): LookBackDate {
  switch (lookBackString) {
    case "24 hours":
      return subHours(present, 24); // within the last... 24 hours
    case "1 week":
      return subWeeks(present, 1); // within the last... 1 week
    case "2 weeks":
      return subWeeks(present, 2); // within the last... 2 weeks
    case "1 month":
      return subMonths(present, 1); // within the last... 1 month
    case "3 months":
      return subMonths(present, 3); // within the last... 3 months
    case "6 months":
      return subMonths(present, 6); // within the last... 6 months
    case "1 year":
      return subYears(present, 1); // within the last... 1 year
    default:
      return null;
  }
}

export function getTimeUnit(timestamp: Date): "minute" | "hour" | "day" {
  const now = new Date();

  let ago: Date;

  ago = subHours(now, 1);
  const withinLastHour = isWithinRange(timestamp, {
    start: ago,
    end: now
  });

  if (withinLastHour) return "minute";

  ago = subHours(now, 24);
  const withinLastDay = isWithinRange(timestamp, {
    start: ago,
    end: now
  });

  if (withinLastDay) return "hour";

  return "day";
}

interface AJAXErrorValues {
  status: number | undefined;
  method: string | undefined;
  url: string | undefined;
}

export function ajaxErrorHandler(data: AJAXErrorValues): PageLoadError {
  let msg: string;

  if (isValidData(data.method) && isValidData(data.url)) {
    msg = `${data.method.toUpperCase()} request to '${data.url}' failed`;
  } else {
    msg = "API request failed";
  }

  msg += isValidData(data.status)
    ? ` with an error-code of ${data.status}.`
    : ".";

  return {
    code: isValidData(data.status) ? data.status : 500,
    endpoint: isValidData(data.url) ? data.url : "Unknown API endpoint",
    summary: msg
  };
}

export function extractAJAXErrorValues(error: AxiosError): AJAXErrorValues {
  // Extract what's desired from the AxiosError object
  const { response, config } = error;

  return {
    status: response ? response.status : undefined,
    method: config?.method,
    url: config?.url
  };
}

export function validateProfileData(data: UserProfileData): boolean {
  return isValidString(data.firstname) && isValidString(data.lastname);
}

export function groupReasonCode(
  data: ICaseReasonCode[]
): IGroupedCaseReasonCode {
  const initial: IGroupedCaseReasonCode = {
    bank: [],
    card: [],
    disc: [],
    sams: [],
    void: []
  };

  return data.reduce((memo, current) => {
    const key = current.risk_pattern;
    memo[key].push(current);
    return memo;
  }, initial);
}

export function translateCaseDeta(data: IDataAPICases): ICaseInfo[] {
  const flattenedData: ICaseInfo[] = [];

  let riskPatternType: keyof IDataAPICases;
  for (riskPatternType in data) {
    const dataByRiskPattern = data[riskPatternType];
    if (Array.isArray(dataByRiskPattern)) {
      dataByRiskPattern.forEach((item: ICaseInfo) => {
        flattenedData.push(item);
      });
    }
  }

  return flattenedData;
}

export function filterParamsRiskPattern(params: any): any {
  const caseStatusValues = (params.caseStatus || [])
    .map((status: SelectOption) => status.value)
    .filter((status: string) => status !== "");

  const payload = {
    page: 1,
    page_size: 15,
    ...(params.assignedTo && { filter_assigned_to: params.assignedTo }),
    ...(params.lookBackTextValue && { filter_date: params.lookBackTextValue }),
    ...(params.stores && {
      filter_store: params.stores.map((store: ICaseStore) => store.store_code)
    }),
    ...(params.countries && {
      filter_country: params.countries.map(
        (country: ICaseCountry) => country.country_code
      )
    }),
    ...(params.riskLevel && {
      filter_risk_score: params.riskLevel.map((riskScore: string) =>
        riskScore.toUpperCase()
      )
    }),
    ...(caseStatusValues.length > 0 && {
      filter_case_status: caseStatusValues
    }),
    ...(params.reasonCodes && {
      filter_reason_code: params.reasonCodes.map(
        (option: ICaseReasonCode) => option.reason_code
      )
    }),
    ...(params.riskPatterns && {
      filter_risk_pattern: params.riskPatterns.map((option: RiskPatternItem) =>
        option.id.toUpperCase()
      )
    }),
    ...(isValidData(params.overdue) && { filter_overdue: params.overdue }),
    ...(params.search && { filter_search: params.search }),
    sort_column: "risk_score",
    sort_direction: "asc"
  };
  return payload;
}

export function filterParamsRiskControl(params: any) {
  const payload = {
    offset: 0, //((params.paging.page - 1) * params.paging.page_size) || 0,
    limit: params.paging.page_size || 15,
    sort: params.paging.sort || CONTROLS_SORT_BY.RISK_LEVEL,
    sort_ascending: params.paging.sort_ascending,
    ...(params.status && {
      case_statuses: params.status.map((s: any) => s.value)
    }),
    ...(params.riskLevel && {
      risk_levels: params.riskLevel.map(capitalize)
    }),
    ...(params.lookBackTextValue && getDateRange(params.lookBackTextValue)),
    ...(params.purchaseTenders && { purchase_tenders: params.purchaseTenders }),
    ...(params.returnTenders && { return_tenders: params.returnTenders }),
    ...(params.amountValue &&
      getMinMaxAmountFilter("return", params.amountValue)),
    ...(params.amountOriginalValue &&
      getMinMaxAmountFilter("original", params.amountOriginalValue)),
    ...(params.amountRefundValue &&
      getMinMaxAmountFilter("refund", params.amountRefundValue)),
    ...(params.amountPriceAdjValue &&
      getMinMaxAmountFilter(
        "price_adjustment_amount",
        params.amountPriceAdjValue
      )),
    ...(params.countries && {
      countries: params.countries.map((country: any) => {
        const countryCode =
          "country_code" in country
            ? country.country_code
            : country.value.split(".")[1];
        return countryCode;
      })
    }),
    ...(params.stores && {
      stores: params.stores.map((store: ICaseStore) => store.store_code)
    }),

    ...(params.assignedTo && { assigned_to: params.assignedTo }),
    ...(params.reasonCodes && { risk_reasons: params.reasonCodes }),
    ...(isValidData(params.overdue) && { overdue: params.overdue }),
    ...(params.search && { freetext: params.search }),
    ...(params.riskControlType && {
      risk_control_types: getRiskControlType(
        params.riskControlType,
        params.filterControlType
      )
    })
    //     ...(params.filterControlType && params.filterControlType.length && { risk_control_types: params.filterControlType.map((item: any) => item.value)})
  };
  return payload;
}

function getRiskControlType(riskControlType: string, filterControlType: any) {
  let type: any;
  if (
    riskControlType === RC.ANA_ROUTE_PARAM &&
    filterControlType &&
    filterControlType.length
  ) {
    type = [...filterControlType.map((c: any) => c.value)];
  } else {
    type = getControlApiCodeByRoute(riskControlType);
  }
  return type;
}
/**
 * Tags the case-item with a new property, risk_pattern.
 * This is useful for other operations downstream to easily
 * be able to distinguish each case-item by their risk-pattern-type
 * @param caseItem object returned as part of the server API response
 * @param riskPattern one of the following string literals: "bank" | "card" | "disc" | "samss" | "void"
 */
export function tagRiskPattern<O extends ICaseInfo, K extends string>(
  caseItem: O,
  riskPattern: K
): O {
  return {
    ...caseItem,
    risk_pattern: riskPattern as keyof IDataAPICases
  };
}

export function extractRiskPatternType(caseid: string): string {
  if (!isValidString(caseid)) return "";

  const RISK_PATTERN_LENGTH = 4;
  const extracted = caseid.substr(0, RISK_PATTERN_LENGTH);

  if (extracted.length !== RISK_PATTERN_LENGTH) return "";

  return extracted.toLowerCase();
}

export function isExemptLocaleCurrency(value: Country): boolean {
  return value === "JP";
}

/**
 * Formats the first param and returns it decorated with the
 * currency symbol associated with the secound param
 * @param value Numeric value to be formatted with currency symbol/code
 * @param country The ISO-3166 country code
 */
export function getLocaleCurrency(
  value: number | string,
  country: string,
  showCurrencySymbol = true
): string {
  if (!isValidNumber(value)) return "N/A";

  const key = Object.keys(CurrencyLocaleMapping).find(
    (lookupKey): lookupKey is Country => lookupKey === country
  );

  if (!key) {
    throw new Error(
      `Unknown country code '${key}' supplied. Please try another.`
    );
  }

  const mapping = CurrencyLocaleMapping[key];

  const formatter = new Intl.NumberFormat(mapping.locale, {
    style: "currency",
    currency: mapping.currency,
    minimumFractionDigits:
      showCurrencySymbol && !isExemptLocaleCurrency(key) ? 2 : 0
  });

  if (showCurrencySymbol) {
    return formatter.format(value);
  } else {
    /**
     * Attribution:
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/formatToParts
     *
     */
    return formatter
      .formatToParts(value)
      .map(({ type, value }) => {
        switch (type) {
          case "currency":
            return "";
          case "literal":
            return "";
          default:
            return value;
        }
      })
      .reduce((string, part) => string + part);
  }
}

export function vueFilterLocalizeCurrency(
  value: number | string,
  country: string,
  showCurrencySymbol = true
): string {
  return getLocaleCurrency(value, country, showCurrencySymbol);
}

// Attribution: https://vuejs.org/v2/guide/filters.html
export function vueFilterCapitalize(value: unknown): unknown {
  if (!value || typeof value !== "string") return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Handler function for logic-branching errors.
 * The use-case which has inspired it is the
 * 'default' case of a switch statement.
 * @param message Error message
 * @param token input which caused this function to be called
 */
export function neverError(
  message: string,
  token: never // The culprit
) {
  return new Error(`${message}. ${token} should not exist`);
}

export function vueFilterNumberParse(value: unknown): string | number {
  return isValidNumber(value) ? +value : "N/A";
}

export function vueFilterNotAvailable(value: unknown): string {
  return isValidData(value)
    ? isValidString(value) || isValidNumber(value)
      ? value.toString()
      : "N/A"
    : "N/A";
}

/*
 * For creating rca-app-server API endpoint URLs.
 * @param pathname should always begin with a forward-slash (/)
 */

export function createServerEndpoint(pathname: string): string {
  // Should NOT have a trailing slash (at the end of the string)
  const serverBase: string = import.meta.env.VITE_APP_API_HOST;

  return isValidString(serverBase)
    ? serverBase + pathname
    : "http://localhost:4000" + pathname;
}

export function isWithinAmountRange(
  range: string,
  columnValue: string
): boolean {
  switch (range) {
    case AMOUNT_RANGE_OPTIONS.LESS_THAN_100_VALUE:
      return Number(columnValue) < 100;
    case AMOUNT_RANGE_OPTIONS.MORE_THAN_1000_VALUE:
      return Number(columnValue) > 1000;
    case AMOUNT_RANGE_OPTIONS.RANGE_500_TO_999_VALUE:
      return Number(columnValue) >= 500 && Number(columnValue) <= 999;
    case AMOUNT_RANGE_OPTIONS.RANGE_100_TO_200_VALUE:
      return Number(columnValue) >= 101 && Number(columnValue) <= 499;
    default:
      return false;
  }
}

export function isDifferent<T>(
  changedValue: T[],
  originalValue: T[],
  field: keyof T
) {
  return (changedValue || [])
    .filter(
      (val: T) => !(originalValue || []).some((v: T) => v[field] == val[field])
    )
    .concat(
      (originalValue || []).filter(
        (val: T) => !(changedValue || []).some((v: T) => v[field] == val[field])
      )
    ).length;
}

export function asyncIvoke(fn: any, params: any) {
  setTimeout(() => {
    fn(params);
  });
}

export function getDateRange(dateValue: string) {
  const DAYS = "days";
  const MONTHS = "months";
  const YEARS = "years";
  const DAT_FORMAT_YYYY_MM_DD = "YYYY-MM-DD";
  const LEGACY_ALL_TIME_VALUE = "";
  const LEGACY_ALL_TIME_TEXT = "All-time";

  const getOneYearBackDate = () =>
    moment()
      .subtract(1, YEARS)
      .format(DAT_FORMAT_YYYY_MM_DD);

  const formattedDate = {
    start_date: getOneYearBackDate(),
    end_date: moment().format(DAT_FORMAT_YYYY_MM_DD)
  };

  switch (dateValue) {
    case LEGACY_ALL_TIME_VALUE:
    case LEGACY_ALL_TIME_TEXT:
    case DATE_RANGE_OPTIONS.ONE_YEAR_VALUE:
      // Legacy "All-time" values are intentionally mapped to the new max window.
      formattedDate.start_date = getOneYearBackDate();
      break;
    case DATE_RANGE_OPTIONS.LAST_24_HRS_VALUE:
      formattedDate.start_date = moment()
        .subtract(1, DAYS)
        .format(DAT_FORMAT_YYYY_MM_DD);
      break;
    case DATE_RANGE_OPTIONS.ONE_WEEK_VALUE:
      formattedDate.start_date = moment()
        .subtract(7, DAYS)
        .format(DAT_FORMAT_YYYY_MM_DD);
      break;
    case DATE_RANGE_OPTIONS.TWO_WEEK_VALUE:
      formattedDate.start_date = moment()
        .subtract(14, DAYS)
        .format(DAT_FORMAT_YYYY_MM_DD);
      break;
    case DATE_RANGE_OPTIONS.ONE_MONTH_VALUE:
      formattedDate.start_date = moment()
        .subtract(1, MONTHS)
        .format(DAT_FORMAT_YYYY_MM_DD);
      break;
    case DATE_RANGE_OPTIONS.THREE_MONTHS_VALUE:
      formattedDate.start_date = moment()
        .subtract(3, MONTHS)
        .format(DAT_FORMAT_YYYY_MM_DD);
      break;
    case DATE_RANGE_OPTIONS.SIX_MONTHS_VALUE:
      formattedDate.start_date = moment()
        .subtract(6, MONTHS)
        .format(DAT_FORMAT_YYYY_MM_DD);
      break;
    default:
      break;
  }
  return formattedDate;
}

export function capitalize(arg: string): string {
  const result = arg && arg.charAt(0).toUpperCase() + arg.slice(1);
  return result;
}

export function formatPurchaseTenderType(
  purchaseTenderType: string,
  purchaseExternalTenderType: string
): string {
  const result =
    (purchaseTenderType || "") +
    (purchaseTenderType && purchaseExternalTenderType ? " - " : "") +
    (purchaseExternalTenderType || "");
  return result;
}

export function getControlNameByRoute(controlRoute: string): string {
  return RiskControlsRouteParamMap[controlRoute];
}

export function getCompensationDescByCode(code: string): string {
  return RiskControlsCompensationTypes[code];
}

export function getControlApiCodeByRoute(controlRoute: string): any {
  return RiskControlsMap.find(
    (control: any) => control.routeParam === controlRoute
  )?.code;
}

export function getControlRouteByCode(controlCode: string): any {
  return RiskControlsMap.find((control: any) =>
    control.code.includes(controlCode)
  )?.routeParam;
}

export function getAmountReferenceList() {
  return JSON.parse(JSON.stringify(AMOUNT_REFERENCE_LIST));
}

export function getMinMaxAmountFilter(fieldPrefix: string, value: string) {
  let minAmount = {};
  switch (value) {
    case AMOUNT_RANGE_OPTIONS.LESS_THAN_100_VALUE:
      minAmount = {
        [`min_${fieldPrefix}_amount`]: 0,
        [`max_${fieldPrefix}_amount`]: 100
      };
      break;
    case AMOUNT_RANGE_OPTIONS.RANGE_100_TO_200_VALUE:
      minAmount = {
        [`min_${fieldPrefix}_amount`]: 101,
        [`max_${fieldPrefix}_amount`]: 499
      };
      break;
    case AMOUNT_RANGE_OPTIONS.RANGE_500_TO_999_VALUE:
      minAmount = {
        [`min_${fieldPrefix}_amount`]: 500,
        [`max_${fieldPrefix}_amount`]: 999
      };
      break;
    case AMOUNT_RANGE_OPTIONS.MORE_THAN_1000_VALUE:
      minAmount = {
        [`min_${fieldPrefix}_amount`]: 1000,
        [`max_${fieldPrefix}_amount`]: null
      };
      break;
    default:
      break;
  }
  return minAmount;
}

export function formatNumberWithSuffix(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return "";

  if (num >= 1e9) {
    return (num / 1e9).toFixed(1) + "B";
  } else if (num >= 1e6) {
    return (num / 1e6).toFixed(1) + "M";
  } else if (num >= 1e3) {
    return (num / 1e3).toFixed(1) + "K";
  } else {
    return num.toString();
  }
}

export function isEnabledRelatedCases(): boolean {
  return false; // process.env.RELATED_CASES_FEATURE_FLAG;
}

export function hasGermanyinCountry(countries: any): boolean {
  if (!countries?.countries) return false;

  return countries.countries.some((country: any) => {
    const countryCode = country.country_code ||
      (country.value?.split('.')[1]) || '';
    return countryCode.toLowerCase() === 'de';
  });
}
